import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Download } from 'lucide-react';

export default function History() {
  const [tab, setTab] = useState<'lookups' | 'messages'>('lookups');
  const [lookups, setLookups] = useState<any[]>([]);
  const [messages, setMessages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    if (tab === 'lookups') {
      api
        .lookups({ limit: 100 })
        .then((r) => setLookups(r.results || []))
        .finally(() => setLoading(false));
    } else {
      api
        .messages({ limit: 100 })
        .then((r) => setMessages(r.results || []))
        .finally(() => setLoading(false));
    }
  }, [tab]);

  const download = (type: 'lookups' | 'messages') => {
    const token = localStorage.getItem('token');
    const url = type === 'lookups' ? api.exportLookups() : api.exportMessages();
    // Open with auth via fetch + blob for proper download
    fetch(url, { headers: { Authorization: `Bearer ${token}` }, credentials: 'include' })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${type}.csv`;
        a.click();
      });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">History</h1>
          <p className="text-muted-foreground mt-1">Lookup and message logs</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => download(tab)}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      <div className="flex gap-2">
        <Button variant={tab === 'lookups' ? 'default' : 'outline'} onClick={() => setTab('lookups')}>
          Lookups
        </Button>
        <Button
          variant={tab === 'messages' ? 'default' : 'outline'}
          onClick={() => setTab('messages')}
        >
          Messages
        </Button>
      </div>

      {loading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : tab === 'lookups' ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Lookup history</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="pb-2 pr-4">Number</th>
                  <th className="pb-2 pr-4">Carrier</th>
                  <th className="pb-2 pr-4">Type</th>
                  <th className="pb-2 pr-4">When</th>
                </tr>
              </thead>
              <tbody>
                {lookups.map((l) => (
                  <tr key={l.id} className="border-b border-border/40">
                    <td className="py-2 pr-4 font-mono">{l.national_format || l.phone_number}</td>
                    <td className="py-2 pr-4">
                      {l.is_verizon ? (
                        <Badge variant="verizon">Verizon</Badge>
                      ) : (
                        l.carrier_name || '—'
                      )}
                    </td>
                    <td className="py-2 pr-4">{l.line_type || '—'}</td>
                    <td className="py-2 pr-4 text-muted-foreground">
                      {new Date(l.created_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {lookups.length === 0 && (
              <p className="text-muted-foreground py-4">No lookups yet.</p>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Message history</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="pb-2 pr-4">To</th>
                  <th className="pb-2 pr-4">Status</th>
                  <th className="pb-2 pr-4">Body</th>
                  <th className="pb-2 pr-4">When</th>
                </tr>
              </thead>
              <tbody>
                {messages.map((m) => (
                  <tr key={m.id} className="border-b border-border/40">
                    <td className="py-2 pr-4 font-mono text-xs">{m.gateway_email}</td>
                    <td className="py-2 pr-4">
                      <Badge
                        variant={
                          m.status === 'sent'
                            ? 'success'
                            : m.status === 'failed'
                              ? 'destructive'
                              : 'secondary'
                        }
                      >
                        {m.status}
                      </Badge>
                    </td>
                    <td className="py-2 pr-4 max-w-[200px] truncate">{m.body}</td>
                    <td className="py-2 pr-4 text-muted-foreground">
                      {new Date(m.created_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {messages.length === 0 && (
              <p className="text-muted-foreground py-4">No messages yet.</p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
